// desktopNotifications - the in-app notification feed, mirrored to the OS.
//
// A run takes minutes, so the analyst starts one and goes somewhere else. The
// bell is only useful to someone already looking at this tab; this is the same
// events reaching a reader who is not.
//
// Three deliberate constraints:
//
//   - OPT-IN, on a click. Browsers gate Notification.requestPermission behind a
//     user gesture, and a permission prompt fired on page load is the thing
//     that gets a site's notifications denied forever. The preference below is
//     what a reader turns on; the browser grant is what the browser asks for at
//     that moment, and both have to hold.
//   - SILENT WHILE WATCHING. An OS banner for something already rendering in
//     the bell three inches away is noise, so a notification is only raised
//     while this document is hidden.
//   - NO STATE OF ITS OWN. Titles, bodies and links come from the notifier's
//     rows, so the banner and the feed can never say different things.
//
// Nothing here uses a service worker: these are page notifications, and they
// last as long as the tab does. Delivery to a CLOSED tab would need push
// subscriptions and a VAPID key on the orchestrator, which is a different
// feature from this one.

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * What the reader can be told about desktop notifications right now.
 *
 * `blocked` is separate from `off` because they need different copy: `off` is
 * fixed by our toggle, `blocked` can only be fixed in browser settings, and
 * offering a toggle that silently does nothing is worse than saying so.
 */
export type DesktopNotifyStatus = 'unsupported' | 'blocked' | 'off' | 'on';

const STORAGE_KEY = 'voltz-desktop-notifications';

/** What a raised notification carries. `tag` collapses a redelivery of the
 *  same row onto the banner already on screen instead of stacking a second. */
export interface DesktopNotification {
  tag: string;
  title: string;
  body?: string | null;
  onClick?: () => void;
  /**
   * Show even while this tab is in the foreground.
   *
   * Only a banner the reader asked for by clicking sets this. An event-driven
   * one must not: the bell is on screen showing the same row, and duplicating
   * it in the corner of the OS is the behaviour that gets a site's
   * notifications switched back off.
   */
  whileVisible?: boolean;
}

function storedPreference(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    // localStorage unavailable
    return false;
  }
}

function storePreference(on: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    // localStorage unavailable
  }
}

function currentStatus(): DesktopNotifyStatus {
  // isSecureContext, because the constructor exists on an http:// origin and
  // then permission is permanently 'denied' - which would render as "you
  // blocked us" rather than "this deployment has no TLS".
  if (typeof window === 'undefined' || !('Notification' in window) || !window.isSecureContext) {
    return 'unsupported';
  }
  if (Notification.permission === 'denied') return 'blocked';
  return storedPreference() && Notification.permission === 'granted' ? 'on' : 'off';
}

/** How much of a body an OS banner is given. Two lines' worth on every shell
 *  that clamps, which is all of them; past it the banner is truncated anyway
 *  and the feed row is the place to read the rest. */
const BODY_MAX = 180;

/**
 * The body, made safe to hand to the OS.
 *
 * A failed run's body is the agent's own failure detail, which originates
 * INSIDE the analysis VM and reaches here unbounded. In the page it lands in a
 * line-clamp-2 block; an OS banner has no such frame, so a sample that can
 * steer its agent's goodbye text would otherwise choose the length, the line
 * count and the bidi/control characters of something the analyst reads outside
 * the page under this product's name. Collapsing whitespace and clamping keeps
 * the banner saying what the row says and nothing more.
 */
function bannerBody(body: string | null | undefined): string | undefined {
  if (!body) return undefined;
  const flat = body.replace(/\s+/gu, ' ').trim();
  if (flat === '') return undefined;
  return flat.length > BODY_MAX ? `${flat.slice(0, BODY_MAX - 1)}\u2026` : flat;
}

// The sound.
//
// The Notification API cannot ask for one. Its only audio control is `silent`,
// and that goes one way - true forces silence, the default "respects the
// device's default settings". Whether a banner makes a noise is therefore the
// OS's decision, per application: Firefox on Windows hands the toast to the
// Windows notification server, and a page cannot reach the switch that decides
// whether Windows plays anything for Firefox. A reader who turns alerts on and
// hears nothing is the ordinary outcome, not a misconfiguration.
//
// So the page plays it - through a MEDIA ELEMENT, which is the whole reason
// this is not Web Audio. Chrome SUSPENDS an AudioContext while its tab is
// hidden, and a hidden tab is the only state in which an alert ever fires, so
// a scheduled oscillator is silent exactly when it is needed; the first
// version of this shipped that way and only ever made a noise for the
// foreground test banner. A media element is not suspended by visibility,
// which is why a backgrounded tab can go on playing a video.
//
// Autoplay still applies: playback needs the site to have had a user
// interaction. The opt-in click is that interaction, and it is also when the
// element is primed - played through once at zero volume, which settles the
// permission question and puts the file in cache, so the alert itself is never
// waiting on a fetch.
//
// `silent` is then set from what priming PROVED, not from what it hoped: the
// banner is silenced only while this page is known to be able to play, so
// there is one noise rather than none or two, and a tab that cannot play
// leaves the banner un-silenced and the OS deciding as before.

/** Same-origin so the document's CSP admits it: the policy sets no media-src
 *  and default-src is 'self', which a data: URL would fail. */
const ALERT_SOUND = '/alert.wav';

let alertAudio: HTMLAudioElement | null = null;
let canSound = false;
let primed = false;

/**
 * Prepares the alert sound, from a user gesture.
 *
 * Safe to call on any interaction, and does its work once: the probe plays the
 * one shared element, and a second probe overlapping the first would zero the
 * volume of an alert already sounding through it - the single outcome the
 * `silent` coupling exists to prevent. A probe that came back refused unlatches
 * so a later gesture can try again; there is nothing to retry after one that
 * succeeded.
 *
 * Inaudible by construction. `muted` as well as `volume`, because volume is
 * not writable on WebKit for iOS, where the assignment is ignored and the
 * probe would play the alert at system volume on the opt-in click. Muted
 * playback is weaker evidence in Chrome, which permits it without activation -
 * but every caller is a gesture handler, so the document holds activation
 * either way and the verdict stands.
 */
export function primeAlertSound(): void {
  if (primed) return;
  if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
  primed = true;
  if (!alertAudio) {
    alertAudio = new Audio(ALERT_SOUND);
    alertAudio.preload = 'auto';
  }
  const probe = alertAudio;
  probe.muted = true;
  probe.volume = 0;
  void startPlayback(probe).then((started) => {
    probe.pause();
    probe.currentTime = 0;
    probe.muted = false;
    probe.volume = 1;
    // Autoplay refused, or no output device. The banner then keeps whatever
    // sound the OS gives it, which beats being silenced for a noise this page
    // cannot make.
    canSound = started;
    if (!started) primed = false;
  });
}

/**
 * Starts playback and reports whether it began.
 *
 * `play()` is not uniformly a promise. Older engines return undefined, and one
 * with no media implementation at all throws from the call - both are "this
 * page makes no sound", and neither may surface as an exception inside a
 * stream handler. An engine that returns nothing is taken at its word, since
 * there is nothing else to go on.
 */
function startPlayback(el: HTMLAudioElement): Promise<boolean> {
  try {
    const started: unknown = el.play();
    if (!(started instanceof Promise)) return Promise.resolve(true);
    return started.then(
      () => true,
      () => false,
    );
  } catch {
    return Promise.resolve(false);
  }
}

/** Plays the alert. A refused play means the permission lapsed, so the next
 *  banner stops being silenced on this page's behalf. */
function playAlertSound() {
  if (!alertAudio) return;
  alertAudio.currentTime = 0;
  void startPlayback(alertAudio).then((started) => {
    if (!started) canSound = false;
  });
}

// One alert per event, not one per tab.
//
// An analyst keeps several dashboard tabs open - a report, the submissions
// list, a console - and every one of them holds the same notification stream.
// Without this, a finished run banners and sounds once per hidden tab.
//
// The claim is a timestamp in localStorage, which is shared across every tab
// of the origin and readable synchronously, so the decision can be made inline
// where the banner is raised. It is a claim rather than a lock: there is no
// atomic compare-and-set in storage, so two tabs that reach it in the same
// instant can both win. That is the right trade here - the events arrive over
// separate connections milliseconds apart, and the failure it risks is one
// duplicate banner rather than a missed alert.

const CLAIM_KEY = 'voltz-alert-claims';

/** How long a claim stands. Long enough to cover a stream resuming with a
 *  Last-Event-ID in another tab, short enough that the map stays small. */
const CLAIM_TTL_MS = 60_000;

/**
 * A claim this browser could have written inside the TTL.
 *
 * Bounded on BOTH sides. A one-sided `now - at < TTL` treats a stamp in the
 * FUTURE as live forever: `1e999` parses to Infinity, a same-origin write can
 * name any epoch, and a clock that steps back during an NTP correction or a VM
 * resume produces one by accident. Any of those would be carried through every
 * prune and would switch the alert off for that event permanently, while the
 * toggle went on reporting that alerts are armed.
 */
function isLiveClaim(at: unknown, now: number): at is number {
  return (
    typeof at === 'number' && Number.isFinite(at) && at <= now && now - at < CLAIM_TTL_MS
  );
}

/** The live claims, with everything expired or unusable already dropped. */
function readClaims(now: number): Record<string, number> {
  const raw = localStorage.getItem(CLAIM_KEY);
  if (raw === null) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Not ours, or truncated. Read as empty so the next claim overwrites it:
    // a value that can only throw would otherwise disable the dedupe for this
    // browser profile for good, with no path that repairs it.
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
  const claims: Record<string, number> = {};
  for (const [key, at] of Object.entries(parsed)) {
    if (isLiveClaim(at, now)) claims[key] = at;
  }
  return claims;
}

/**
 * Whether THIS tab is the one that alerts for `tag`.
 *
 * Writing back carries only what `readClaims` kept, so the entry stays bounded
 * by the notification rate rather than by how long the browser has been open.
 */
function claimAlert(tag: string): boolean {
  try {
    const now = Date.now();
    const claims = readClaims(now);
    if (claims[tag] !== undefined) return false;
    claims[tag] = now;
    localStorage.setItem(CLAIM_KEY, JSON.stringify(claims));
    return true;
  } catch {
    // Storage blocked. Tabs cannot agree, so every one alerts: told twice
    // beats not told.
    return true;
  }
}

/** Hands the event back, for a tab that claimed it and then showed nothing.
 *  Claim and show have to be paired, or one tab's failure becomes everyone's. */
function releaseClaim(tag: string) {
  try {
    const claims = readClaims(Date.now());
    if (claims[tag] === undefined) return;
    delete claims[tag];
    localStorage.setItem(CLAIM_KEY, JSON.stringify(claims));
  } catch {
    // Storage went away between claiming and releasing. The claim expires on
    // its own inside the TTL.
  }
}

/**
 * Raises one OS notification. Returns whether it was actually shown.
 *
 * Callers do not have to check the status first - a call that is not allowed to
 * show anything is a no-op, which keeps the decision in one place.
 */
export function showDesktopNotification(n: DesktopNotification): boolean {
  if (currentStatus() !== 'on') return false;
  // Already on screen in the bell. See the header.
  if (!n.whileVisible && document.visibilityState === 'visible') return false;
  // A banner the reader asked for in THIS tab is theirs; an event-driven one
  // goes to whichever tab claims it first.
  if (!n.whileVisible && !claimAlert(n.tag)) return false;
  const sounding = canSound;
  try {
    const note = new Notification(n.title, {
      body: bannerBody(n.body),
      tag: n.tag,
      icon: '/favicon.png',
      badge: '/favicon.png',
      // Silenced only when this page is about to make the noise itself.
      silent: sounding,
    });
    note.onclick = () => {
      window.focus();
      note.close();
      n.onClick?.();
    };
    if (sounding) playAlertSound();
    return true;
  } catch {
    // Some engines (Chrome on Android) only allow notifications through a
    // service worker registration and throw from the constructor. There is no
    // recovery and nothing the reader can do, so the feed in the bell stays
    // the only surface - it is never a reason to break the caller. The claim
    // goes back, so it is never held by a tab that showed nothing.
    if (!n.whileVisible) releaseClaim(n.tag);
    return false;
  }
}

/**
 * Raises the one banner a reader asks for on purpose. Returns whether the OS
 * took it.
 *
 * The whole feature is invisible by design - it only ever fires on a tab
 * nobody is looking at - so without this there is no way to find out whether a
 * grant, an OS focus mode or a browser setting silently ate it, short of
 * starting a real analysis and walking away. A false return is worth surfacing
 * to the reader; it means the browser accepted the permission and then refused
 * the notification.
 */
export function sendTestNotification(): boolean {
  return showDesktopNotification({
    tag: 'voltz-test',
    title: 'Foresee Sandbox',
    body: 'Desktop alerts are working. This is what a finished run looks like.',
    whileVisible: true,
  });
}

/**
 * Desktop-notification state plus the two controls that change it.
 *
 * `enable` must be called from a user gesture: it is what triggers the
 * browser's permission prompt. It resolves to the status it settled on, so a
 * caller can react to a denial in the same handler.
 */
export function useDesktopNotifications(): {
  status: DesktopNotifyStatus;
  enable: () => Promise<DesktopNotifyStatus>;
  disable: () => void;
} {
  const [status, setStatus] = useState<DesktopNotifyStatus>(currentStatus);

  // The grant can be revoked from browser settings while the tab is open, and
  // nothing tells the page. Re-reading when the tab comes back is enough to
  // keep the toggle honest without polling a permission.
  useEffect(() => {
    const sync = () => setStatus(currentStatus());
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  // The audio context has to be opened from a user gesture, and the gesture
  // that turns alerts on is usually in a different page load from the run that
  // finishes. So while alerts are on, the next interaction anywhere in the
  // document opens it, once. Without this a reloaded tab has a banner and no
  // sound - and, worse, would have silenced the OS banner for a chime it could
  // not play, which is why `sounding` is read per notification rather than
  // assumed.
  useEffect(() => {
    if (status !== 'on') return;
    const prime = () => primeAlertSound();
    document.addEventListener('pointerdown', prime, { once: true });
    document.addEventListener('keydown', prime, { once: true });
    return () => {
      document.removeEventListener('pointerdown', prime);
      document.removeEventListener('keydown', prime);
    };
  }, [status]);

  const enable = useCallback(async (): Promise<DesktopNotifyStatus> => {
    if (currentStatus() === 'unsupported') {
      setStatus('unsupported');
      return 'unsupported';
    }
    // Before the await: this handler still holds user activation here, and
    // that is what lets the context start running rather than suspended.
    primeAlertSound();
    if (Notification.permission === 'default') await Notification.requestPermission();
    storePreference(Notification.permission === 'granted');
    // What was ACHIEVED, not what was granted. currentStatus re-reads the
    // permission AND the stored preference, and storePreference swallows a
    // failed write - a browser with storage blocked for the site would
    // otherwise report 'on' to a caller while every later read says 'off',
    // and the confirmation banner would blame the OS for it.
    const next = currentStatus();
    setStatus(next);
    return next;
  }, []);

  const disable = useCallback(() => {
    storePreference(false);
    setStatus(currentStatus());
  }, []);

  return { status, enable, disable };
}

/**
 * Remembers which notification ids have already been raised, for the lifetime
 * of the tab.
 *
 * The stream resumes with a Last-Event-ID after a reconnect, so a row the
 * reader was already told about can arrive a second time. Without this the
 * same completed run banners twice on every flaky network.
 */
export function useNotifiedOnce(): (id: string) => boolean {
  const seen = useRef<Set<string>>(new Set());
  return useCallback((id: string) => {
    if (seen.current.has(id)) return false;
    seen.current.add(id);
    return true;
  }, []);
}
